import { UserRole } from 'gql/types';

// Date range options for dashboard filters
// Rolling options use relative periods (e.g., last 7 days)
// Calendar options use fixed calendar boundaries (e.g., this calendar week)
export type DateRangeType =
  // Rolling periods
  | 'day'
  | 'yesterday'
  | 'week'
  | 'lastWeek'
  | 'month'
  | 'lastMonth'
  | 'quarter'
  | 'lastQuarter'
  | 'year'
  | 'lastYear'
  // Calendar periods
  | 'dayCal'
  | 'yesterdayCal'
  | 'weekCal'
  | 'lastWeekCal'
  | 'monthCal'
  | 'lastMonthCal'
  | 'quarterCal'
  | 'lastQuarterCal'
  | 'yearCal'
  | 'pYearCal'
  // Custom
  | 'custom';

// Display modes for the dashboard
export type DashboardDisplayMode = 'normal' | 'split' | 'inline' | 'trends';

// Interval size for trends mode - determines the data granularity
export type TrendsIntervalSize = 'day' | 'week' | 'month' | 'quarter' | 'year';

// Mapping from current date range to "previous" equivalent for comparison
export const DATE_RANGE_PREVIOUS_MAP: Record<DateRangeType, DateRangeType> = {
  // Rolling periods
  day: 'yesterday',
  yesterday: 'yesterday',
  week: 'lastWeek',
  lastWeek: 'lastWeek',
  month: 'lastMonth',
  lastMonth: 'lastMonth',
  quarter: 'lastQuarter',
  lastQuarter: 'lastQuarter',
  year: 'lastYear',
  lastYear: 'lastYear',
  // Calendar periods
  dayCal: 'yesterdayCal',
  yesterdayCal: 'yesterdayCal',
  weekCal: 'lastWeekCal',
  lastWeekCal: 'lastWeekCal',
  monthCal: 'lastMonthCal',
  lastMonthCal: 'lastMonthCal',
  quarterCal: 'lastQuarterCal',
  lastQuarterCal: 'lastQuarterCal',
  yearCal: 'pYearCal',
  pYearCal: 'pYearCal',
  // Custom
  custom: 'custom',
};

// Filter state for a single dashboard pane
export interface PaneFilters {
  dateRange: DateRangeType;
  customDateRange?: { start: Date; end: Date };
  regionIds: string[]; // Multi-select, empty = all regions
  phlebotomistId?: string; // undefined = current user (for CT role) or all (for Admin/MD)
}

// Filter state for the Phlebotomist Dashboard
export interface PhlebotomistDashboardFilters {
  dateRange: DateRangeType;
  customDateRange?: { start: Date; end: Date };
  regionIds: string[]; // Multi-select, empty = all regions
  phlebotomistId?: string; // undefined = current user (for CT role) or all (for Admin/MD)
  showRegionComparison: boolean;
  showCompanyComparison: boolean;
  // Display mode and comparison pane filters
  displayMode: DashboardDisplayMode;
  comparisonPaneFilters?: PaneFilters; // Only used when displayMode is 'split' or 'inline'
  // Trends mode settings
  trendsIntervalSize?: TrendsIntervalSize; // Only used when displayMode is 'trends'
}

// Discard reason count for the failure reasons chart
export interface DiscardReasonCount {
  reason: string;
  count: number;
}

// Individual product record within a donation
export interface DonationProductRecord {
  type: string;
  status: string;
  orderId?: number | null;
  discardReason?: string | null;
}

// Individual donation record for the recent donations table
export interface DonationRecord {
  id: string;
  shortId: number;
  date: Date;
  donorName: string;
  donorId: string;
  type: 'Single' | 'Double';
  approvedML: number;
  actualML?: number | null;
  collectionSet?: string | null;
  veinSide?: string | null;
  donationUnit?: string | null;
  examinationNotes?: string | null;
  ineligibleReason?: string | null;
  expectedPRBC: number;
  expectedFFPSingle: number;
  expectedFFPDouble: number;
  actualPRBC: number;
  actualFFPSingle: number;
  actualFFPDouble: number;
  status:
    | 'Complete'
    | 'DBL→SGL'
    | 'Ineligible'
    | 'Partial'
    | 'Failed'
    | 'Pending';
  // Additional context fields
  phlebotomistName: string;
  locationName: string;
  regionName: string;
  failureReasons: string[];
  products: DonationProductRecord[];
}

// Stats for a phlebotomist or aggregate group
export interface PhlebotomistStats {
  phlebotomistId: string;
  phlebotomistName: string;
  phlebotomistEmail?: string | null;
  regionId?: string | null;
  regionName?: string | null;
  locationPreference?: string | null; // DEPRECATED: use assignedOfficeId
  assignedOfficeId?: string | null;
  assignedOfficeName?: string | null;

  // Attempted counts
  attemptedDonations: number;
  successfulDonations: number; // Draws that yielded at least some product
  pendingDonations: number; // Donations not yet processed (no products/discards)
  singleDonors: number;
  doubleDonors: number;
  successfulSingleDonors: number; // Single donors with Complete status
  successfulDoubleDonors: number; // Double donors with Complete status

  // Expected products (based on approved donation amounts)
  expectedTotal: number;
  expectedPRBC: number;
  expectedPRBCSingle: number;
  expectedPRBCDouble: number;
  expectedFFPSingle: number;
  expectedFFPDouble: number;

  // Actual products produced
  actualTotal: number;
  actualPRBC: number;
  actualPRBCSingle: number;
  actualPRBCDouble: number;
  actualFFPSingle: number;
  actualFFPDouble: number;

  // Special metric: DBL donors who only yielded single (420+ml approved but 1 pRBC + 1 FFP single)
  dblDonorsSglYield: number;

  // Success rates (percentages 0-100)
  overallSuccessRate: number; // successfulDonations / attemptedDonations * 100
  prbcSuccessRate: number; // actualPRBC / expectedPRBC * 100
  sglDonorsSuccessRate: number; // successful single donors / attempted single donors * 100
  dblDonorsSuccessRate: number; // successful double donors / attempted double donors * 100

  // Goal/Team metrics (for office-based goal calculations)
  teamCount?: number; // Number of CollectionTeam phlebotomists at same office
  totalOfficeSuccessful?: number; // Total successful donations at the office
  donationSharePercent?: number; // This CT's share: successfulDonations / totalOfficeSuccessful * 100
  officeSharePercent?: number; // Equal share: 100 / teamCount

  // Discarded products by reason (split by donation outcome)
  completelyFailedDonations: number;
  completelyFailedDiscards: DiscardReasonCount[];
  partiallyFailedDonations: number;
  partiallyFailedDiscards: DiscardReasonCount[];

  // Recent donations for the table
  recentDonations: DonationRecord[];
}

// Comparison stats for Office/Region/Company averages
export interface ComparisonStats {
  label: string;
  overall: {
    drawSuccessRate: number; // Percentage of draws that yielded any product
    attempted: number;
    pendingDonations?: number; // Donations not yet processed (excluded from rates)
    failures: number; // Draws that failed (ineligible)
    unitSuccessRate: number; // Percentage of expected units actually produced
    expected: number;
    actual: number;
  };
  prbc: {
    unitSuccessRate: number;
    expected: number;
    actual: number;
  };
  ffp: {
    unitSuccessRate: number;
    expected: number;
    actual: number;
  };
}

// Phlebotomist info for the dropdown select
export interface PhlebotomistOption {
  id: string;
  name: string;
  regionId: string | null;
  regionName: string | null;
  locationPreferences: string[]; // Array of location names from user's locationPreferences - DEPRECATED
  assignedOfficeId: string | null;
  assignedOfficeName: string | null;
}

// Location info
export interface LocationOption {
  id: string;
  name: string;
  regionId: string | null;
  regionName: string | null;
}

// Region info for the dropdown select
export interface RegionOption {
  id: string;
  name: string;
}

// Trend data point for time-series charts
export interface TrendStatsDataPoint {
  period: string;
  periodStart: Date;
  periodEnd: Date;
  attemptedDonations: number;
  successfulDonations: number;
  singleDonors: number;
  doubleDonors: number;
  successfulSingleDonors: number;
  successfulDoubleDonors: number;
  expectedTotal: number;
  expectedPRBC: number;
  expectedFFPSingle: number;
  expectedFFPDouble: number;
  actualTotal: number;
  actualPRBC: number;
  actualFFPSingle: number;
  actualFFPDouble: number;
  overallSuccessRate: number;
  prbcSuccessRate: number;
  sglDonorsSuccessRate: number;
  dblDonorsSuccessRate: number;
}

// Goal data for donation collection goals
export interface DonationGoalData {
  goalQty: number; // Target number of successful donations
  periodLabel?: string; // e.g., "Weekly", "Monthly"
  dateRangeStart: Date; // Start of the goal period
  dateRangeEnd: Date; // End of the goal period
  // Team and share metrics
  teamCount: number; // Number of phlebotomists at the location
  officeSharePercent: number; // Goal share percentage (e.g., 47% for office of 2)
  donationSharePercent: number; // Actual donation share (this CT's successful / office total)
  totalOfficeSuccessful: number; // Total successful donations at the office
}

// Dashboard context value type
export interface PhlebotomistDashboardContextValue {
  // Filter state
  filters: PhlebotomistDashboardFilters;
  setFilters: (filters: Partial<PhlebotomistDashboardFilters>) => void;
  setComparisonFilters: (filters: Partial<PaneFilters>) => void;

  // Data - primary pane
  currentStats: PhlebotomistStats | null;
  officeStats: ComparisonStats | null;
  regionStats: ComparisonStats | null;
  companyStats: ComparisonStats | null;
  /** Full aggregated region stats (with discards, donations) for side-by-side cards */
  regionFullStats: PhlebotomistStats | null;
  /** Full aggregated company stats (with discards, donations) for side-by-side cards */
  companyFullStats: PhlebotomistStats | null;
  goalData: DonationGoalData | null; // Goal data for specific phlebotomist

  // Data - previous period (always populated for trend arrows in normal mode)
  previousPeriodStats: PhlebotomistStats | null;

  // Data - comparison pane (only populated when displayMode is 'split' or 'inline')
  comparisonStats: PhlebotomistStats | null;
  comparisonOfficeStats: ComparisonStats | null;
  comparisonRegionStats: ComparisonStats | null;
  comparisonCompanyStats: ComparisonStats | null;
  comparisonGoalData: DonationGoalData | null; // Goal data for comparison pane

  // Trends data (only populated when displayMode is 'trends')
  trendData: TrendStatsDataPoint[];
  trendDataLoading: boolean;

  // Display mode helpers
  setDisplayMode: (mode: DashboardDisplayMode) => void;
  resetDisplayMode: () => void;

  // Dropdown options
  phlebotomists: PhlebotomistOption[];
  regions: RegionOption[];

  // Loading state
  loading: boolean;

  // Current user info
  canSelectPhlebotomist: boolean; // true for Admin/MD/TeamLead, false for CT/Phlebotomist
  currentUserId: string | null;
  isCollectionTeam: boolean; // true if current user has Collection Team role
  isTeamLead: boolean; // true if current user has TeamLead role
  isPhlebotomist: boolean; // true if current user has Phlebotomist role
  /** Whether region filter should be shown (false for TeamLead/Phlebotomist - auto-locked) */
  showRegionFilter: boolean;
  /** Whether phlebotomist filter should be shown (false for Phlebotomist - only own data) */
  showPhlebotomistFilter: boolean;
  /** Whether the current user has a region assigned (productRegionPreference) */
  hasRegion: boolean;
  /** True when admin simulation mode is active */
  isSimulating: boolean;
  /** Simulation details for banner display */
  simulationInfo: {
    role: UserRole;
    userName?: string;
    officeName?: string;
  } | null;
}

// Default filter values
export const DEFAULT_FILTERS: PhlebotomistDashboardFilters = {
  dateRange: 'week',
  regionIds: [],
  showRegionComparison: false,
  showCompanyComparison: false,
  displayMode: 'normal',
};
